/* ═══════════════════════════════════════════════════════════════════
   alj-search.js — Search & filter behaviors · DIRECTION B (Aug 2026)

   1. Metal swatch dots — colors facet values that match known metals
      (hexes from the pipeline's metal table). Re-runs after the theme
      ajax-refreshes the filter UI.
   2. Shape icon tray — tags any facet whose label contains "shape"
      with .alj-shape-facet and injects an outline icon per value
      (Round/Oval/Emerald/Pear/…); unknown shapes get a diamond icon.
   3. Sort control — wires <select data-alj-sort> on the search page
      (updates ?sort_by=, preserves query + filters).
   4. Takeover panel — reveals the TRENDING / NEW IN panel when the
      search input gains focus, highlights trending links that contain
      the typed query. Deliberately API-free (no predictive search on
      Shopify Basic).
   5. PDP metal swatches (v3.0) — turns the theme's Metal <select>
      into a swatch row on product pages of multi-metal variant
      products. The select stays (visually hidden) and swatch clicks
      drive it, so the theme's variant logic keeps working. Grouped
      metal-sibling products already get Grouptify's PDP swatch and
      have no Metal select, so they're naturally unaffected. Ring
      builder pages (/pages/…) are skipped.
   6. Umbrella warranty relocation (v3.3) — on templates where the
      Umbrella app block is NOT placed (the add-diamond engagement
      template), the app auto-injects its coverage widget at the TOP
      of the buy column, above the title. Move it into the content
      column right after the inventory status — the same slot the
      placed block occupies on the jewelry templates. Pages with the
      placed block are untouched (the selector only matches the
      auto-injected top position). alj-search.css hides the widget
      until the move happens so it never flashes at the top.
   7. Legacy Metal-param shim (v3.4) — the S&D Metal filter moved from
      the Metal product option (filter.v.option.metal) to the
      custom.available_metals product metafield. Any old link still
      carrying the option param — emails, ads, bookmarks — is mapped to
      the equivalent metafield value(s) and redirected, so it keeps
      filtering instead of silently showing everything.
   8. Search-page raw facet suppression (v3.4) — S&D shows grouped
      values PLUS the raw ungrouped ones on search results pages only
      (collections are clean). Hide any Style / Setting / Stone Shape /
      Stone Type value that isn't one of the worksheet's group names;
      active raw values from deep links stay visible.
   9. Mobile filter drawer (v3.6) — builds the phone toolbar's single
      FILTERS button + bottom drawer chrome around the existing facets
      wrapper. CSS shows it under 769px only; html.alj-has-drawer marks
      that the chrome exists (fallback: stacked label rows).

   Loaded with `defer` from layout/theme.liquid. No dependencies.
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  if (window.__aljSearchInit) return;   // double-include guard
  window.__aljSearchInit = true;

  /* ─── 1. Metal swatch dots ─────────────────────────────────────── */

  var METAL_HEX = {
    'yellow gold': '#D4AF37',
    'white gold': '#E8E8E8',
    'rose gold': '#E8B4B8',
    'green gold': '#BFD8AF',
    'platinum': '#C0C0C0',
    'sterling silver': '#A8A9AD',
    'silver': '#A8A9AD',
    'vermeil': '#CFB53B',
    'gold vermeil': '#CFB53B',
    'yellow': '#D4AF37',
    'white': '#E8E8E8',
    'rose': '#E8B4B8',
    'yellow/white gold': '#D4AF37'   /* v3.4: two-tone (split dot via CSS) */
  };

  function cleanLabel(raw) {
    if (!raw) return '';
    var s = raw.toLowerCase().trim();
    s = s.replace(/^\d+\s*k[tw]?\s+/, '');      // "14K ", "18KT "
    s = s.replace(/^950\s+/, '');               // "950 Platinum"
    s = s.replace(/\s*\(\d[\d,]*\)\s*$/, '');   // trailing "(412)"
    return s;
  }

  function decorateMetalDots(root) {
    (root || document)
      .querySelectorAll('.faceted-filters__filter-link:not(.alj-has-dot):not(.alj-has-icon)')
      .forEach(function (link) {
        var textEl = link.querySelector('.faceted-filters__filter-list-item-text');
        if (!textEl) return;
        var key = cleanLabel(textEl.textContent);
        var hex = METAL_HEX[key];
        if (!hex) return;
        var dot = document.createElement('span');
        dot.className = 'alj-metal-dot';
        if (key.indexOf('/') !== -1) dot.classList.add('alj-metal-dot--split');  /* v3.4: two-tone */
        dot.style.setProperty('--dot', hex);
        dot.setAttribute('aria-hidden', 'true');
        link.insertBefore(dot, textEl);
        link.classList.add('alj-has-dot');
      });
  }

  /* ─── 2. Shape icon tray ───────────────────────────────────────── */

  var SHAPE_SVG = {
    'round':    '<circle cx="20" cy="20" r="13"/><path d="M11 14h18M11 26h18M20 7l-9 7 9 19 9-19z" opacity=".4"/>',
    'oval':     '<ellipse cx="20" cy="20" rx="10" ry="14"/>',
    'emerald':  '<rect x="9" y="7" width="22" height="26" rx="3"/><rect x="14" y="12" width="12" height="16" rx="1.5" opacity=".4"/>',
    'pear':     '<path d="M20 6c8 8 11 13 11 18a11 11 0 0 1-22 0c0-5 3-10 11-18z"/>',
    'marquise': '<path d="M20 5c5 5 8 10 8 15s-3 10-8 15c-5-5-8-10-8-15s3-10 8-15z"/>',
    'heart':    '<path d="M20 33S8 24 8 15a6.5 6.5 0 0 1 12-3 6.5 6.5 0 0 1 12 3c0 9-12 18-12 18z"/>',
    'princess': '<rect x="9" y="9" width="22" height="22" rx="1.5"/>',
    'square':   '<rect x="9" y="9" width="22" height="22" rx="1.5"/>',
    'cushion':  '<rect x="9" y="9" width="22" height="22" rx="7"/>',
    'asscher':  '<path d="M14 8h12l6 6v12l-6 6H14l-6-6V14z"/>',
    'radiant':  '<path d="M13 8h14l5 5v14l-5 5H13l-5-5V13z"/><path d="M17 13h6l4 4v6l-4 4h-6l-4-4v-6z" opacity=".4"/>',
    'baguette': '<rect x="13" y="7" width="14" height="26" rx="1.5"/>',
    'trillion': '<path d="M20 8l13 22H7z" stroke-linejoin="round"/>',
    'triangle': '<path d="M20 8l13 22H7z" stroke-linejoin="round"/>'
  };
  var SHAPE_DEFAULT = '<path d="M20 6l9 8-9 20-9-20z"/>';

  function shapeSvgFor(label) {
    var key = cleanLabel(label);
    for (var name in SHAPE_SVG) {
      if (key.indexOf(name) !== -1) return SHAPE_SVG[name];
    }
    return SHAPE_DEFAULT;
  }

  function decorateShapeFacets(root) {
    (root || document)
      .querySelectorAll('.faceted-filters__details-wrapper')
      .forEach(function (wrap) {
        var summary = wrap.querySelector('.faceted-filters__filter-group-title');
        if (!summary || !/shape/i.test(summary.textContent)) return;
        wrap.classList.add('alj-shape-facet');
        wrap.querySelectorAll('.faceted-filters__filter-link:not(.alj-has-icon)')
          .forEach(function (link) {
            var textEl = link.querySelector('.faceted-filters__filter-list-item-text');
            if (!textEl) return;
            var icon = document.createElement('span');
            icon.className = 'alj-shape-icon';
            icon.setAttribute('aria-hidden', 'true');
            icon.innerHTML = '<svg viewBox="0 0 40 40">' + shapeSvgFor(textEl.textContent) + '</svg>';
            link.insertBefore(icon, link.firstChild);
            link.classList.add('alj-has-icon');
          });
      });
  }

  /* re-decorate after the theme ajax-refreshes the facet UI (and
     swatchify Metal selects that arrive late, e.g. quick-shop) */
  var debounceTimer = null;
  var observer = new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      decorateShapeFacets(document);
      decorateMetalDots(document);
      swatchifyPdpMetal(document);
      relocateUmbrella();
      suppressRawSearchFacetValues(document);
      buildFilterDrawer();
      fitSortSelects();
    }, 120);
  });

  /* ─── 3. Sort control ──────────────────────────────────────────── */

  function wireSort() {
    document.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-alj-sort]');
      if (!sel) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      window.location.assign(url.toString());
    });
  }

  /* v3.6.2: a <select> sizes itself to its LONGEST option, so the short
     displayed value ("FEATURED") floated in a box sized for "PRICE,
     LOW TO HIGH" — dead space between the text and the toolbar's right
     edge, worst on phones next to the FILTERS button. Shrink-fit each
     sort select to its selected option's rendered width. */
  function fitSortSelects() {
    document.querySelectorAll('.alj-search-sort select, .collection-page__sort-filter select')
      .forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (!opt) return;
        var probe = document.createElement('span');
        var cs = getComputedStyle(sel);
        probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;' +
          'font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize +
          ';font-weight:' + cs.fontWeight + ';letter-spacing:' + cs.letterSpacing +
          ';text-transform:' + cs.textTransform + ';';
        probe.textContent = opt.textContent.trim();
        document.body.appendChild(probe);
        var w = Math.ceil(probe.getBoundingClientRect().width);
        probe.remove();
        if (w > 0) sel.style.width = (w + 6) + 'px';   // +6: breathing room
        if (!sel.__aljFit) {
          sel.__aljFit = true;
          sel.addEventListener('change', function () { fitSortSelects(); });
        }
      });
  }

  /* ─── 4. Takeover panel ────────────────────────────────────────── */

  function wirePopularPanel() {
    document.querySelectorAll('.search-flyout .search-form').forEach(function (form) {
      var input = form.querySelector('.search-form__input');
      var panel = form.querySelector('[data-alj-popular]');
      if (!input || !panel) return;

      input.addEventListener('focus', function () {
        form.classList.add('alj-open');
      });

      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        panel.querySelectorAll('a[data-alj-term]').forEach(function (a) {
          var match = q.length >= 2 && a.textContent.toLowerCase().indexOf(q) !== -1;
          a.classList.toggle('alj-match', match);
        });
      });
    });
  }

  /* ─── 5. PDP metal swatches (v3.0) ─────────────────────────────── */

  function prettyMetal(v) {
    if (!v) return v;
    if (/yellow|white|rose|green/i.test(v) && !/gold|silver/i.test(v)) {
      return v + ' Gold';
    }
    return v;
  }

  function swatchifyPdpMetal(root) {
    if (/^\/pages\//.test(window.location.pathname)) return;  // ring builder etc.
    (root || document)
      .querySelectorAll('.product-details__option-wrapper')
      .forEach(function (wrap) {
        if (wrap.classList.contains('alj-swatchified')) return;
        var label = wrap.querySelector('label');
        var sel = wrap.querySelector('select');
        if (!label || !sel) return;
        if (!/^\s*metal\s*$/i.test(label.textContent)) return;
        if (sel.options.length < 2) return;

        var row = document.createElement('div');
        row.className = 'alj-pdp-swatches';

        var valueOut = document.createElement('span');
        valueOut.className = 'alj-pdp-value';

        function sync() {
          valueOut.textContent = ' — ' + prettyMetal(sel.value);
          row.querySelectorAll('.alj-metal-dot--pdp').forEach(function (b) {
            b.classList.toggle('alj-active', b.getAttribute('data-alj-metal') === sel.value);
          });
        }

        Array.prototype.forEach.call(sel.options, function (opt) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'alj-metal-dot alj-metal-dot--pdp';
          b.setAttribute('data-alj-metal', opt.value);
          b.setAttribute('aria-label', prettyMetal(opt.value));
          b.title = prettyMetal(opt.value);
          b.addEventListener('click', function () {
            if (sel.value !== opt.value) {
              sel.value = opt.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            sync();
          });
          row.appendChild(b);
        });

        sel.addEventListener('change', sync);
        label.appendChild(valueOut);
        wrap.appendChild(row);
        wrap.classList.add('alj-swatchified');
        sync();
      });
  }

  /* ─── 6. Umbrella warranty relocation (v3.3) ───────────────────── */

  function relocateUmbrella() {
    var w = document.querySelector('.product-details > .umbrella-warranties');
    if (!w) return;
    var contents = document.querySelector('.product-details .product-contents');
    if (!contents) return;
    var inv = contents.querySelector('.inventory-status');
    if (inv && inv.parentElement) {
      inv.parentElement.insertBefore(w, inv.nextSibling);
    } else {
      contents.appendChild(w);
    }
    w.classList.add('alj-relocated');
  }

  /* ─── 9. Mobile filter drawer (v3.6) ───────────────────────────── */
  /* One FILTERS button beside the sort control on phones; it opens the
     existing facets wrapper as a bottom drawer of stacked accordion
     groups. CSS keys everything off html.alj-has-drawer, added only
     after the chrome is built — no JS, no drawer, and the stacked
     label rows remain as the fallback. Idempotent per piece so the
     theme's ajax re-renders can rebuild any part it wipes. */

  function buildFilterDrawer() {
    var wrap = document.querySelector('.search-page__filter-wrapper, .collection-page__filter-wrapper');
    if (!wrap) return;
    var fw = wrap.querySelector('.faceted-filters__wrapper');
    if (!fw) return;

    function closeDrawer() { document.documentElement.classList.remove('alj-filter-open'); }

    if (!wrap.querySelector('.alj-filter-toggle')) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'alj-filter-toggle';
      btn.setAttribute('aria-label', 'Open filters');
      btn.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M2.5 5h15M5.5 10h9M8 15h4"/></svg>Filters';
      btn.addEventListener('click', function () {
        document.documentElement.classList.add('alj-filter-open');
      });
      wrap.insertBefore(btn, wrap.firstChild);
    }
    if (!fw.querySelector('.alj-drawer-head')) {
      var head = document.createElement('div');
      head.className = 'alj-drawer-head';
      head.innerHTML = '<span>Filters</span><button type="button" class="alj-drawer-close" aria-label="Close filters">×</button>';
      head.querySelector('.alj-drawer-close').addEventListener('click', closeDrawer);
      fw.insertBefore(head, fw.firstChild);
    }
    if (!document.querySelector('.alj-filter-backdrop')) {
      var backdrop = document.createElement('div');
      backdrop.className = 'alj-filter-backdrop';
      backdrop.addEventListener('click', closeDrawer);
      document.body.appendChild(backdrop);
    }
    document.documentElement.classList.add('alj-has-drawer');
  }

  /* ─── 8. Search-page raw facet value suppression (v3.4) ────────── */
  /* S&D applies value GROUPS cleanly on collection pages, but search
     results pages list the groups AND the raw ungrouped values below
     them (steady-state behavior, not index lag — verified: groups
     created weeks apart all leak the same way, only on /search).
     Whitelist the worksheet's group names per facet and hide any
     other value on search pages. Active raw values stay visible so
     an arriving deep link can still be un-ticked. */

  var ALJ_SEARCH_FACET_WHITELIST = {
    'filter.p.m.custom.product_style': [
      'Wedding & Anniversary Bands', 'Everyday Rings', 'Stackable Rings',
      'Statement & Sculptural Rings', 'Solitaire & Engagement',
      'Enhancers, Guards & Jackets', 'Ring Settings (Build Your Own)',
      'Stud Earrings', 'Hoops & Huggies', 'Drops & Dangles',
      'Climbers, Cuffs & Jackets', 'Motif & Novelty Earrings',
      'Pendants & Solitaires', 'Chains & Layering', 'Tennis & Line Bracelets',
      'Bangles & Cuffs', 'Chain & Station Bracelets', 'Charms'
    ],
    'filter.p.m.custom.setting_styles': [
      'Prong', 'Bezel & Flush', 'Pavé & Bead-Set', 'Solitaire',
      'Channel & Bar', 'Halo', 'Cluster & Multi-Stone', 'Unique Settings'
    ],
    'filter.p.m.custom.stone_shapes': [
      'Round', 'Oval', 'Emerald', 'Marquise', 'Baguette', 'Pear',
      'Princess & Square', 'Asscher', 'Cushion', 'Heart', 'Multi-Shape',
      'Unique Shapes'
    ],
    'filter.v.option.gemstone': [
      'Diamonds', 'Lab Grown Diamonds', 'Gemstones', 'Lab Grown Gemstones'
    ]
  };

  function suppressRawSearchFacetValues(root) {
    if (!document.querySelector('.search-page__filter-wrapper')) return;
    (root || document)
      .querySelectorAll('.faceted-filters__filter-list')
      .forEach(function (ul) {
        var probe = ul.querySelector('.faceted-filters__filter-link[data-name]');
        var allowed = probe && ALJ_SEARCH_FACET_WHITELIST[probe.getAttribute('data-name')];
        if (!allowed) return;
        var seen = {};   // some RAW values share their group's exact name
                         // (raw "Prong" under group "Prong") — the group
                         // renders first, so hide later same-label rows too
        ul.querySelectorAll('.faceted-filters__filter-list-item').forEach(function (li) {
          var a = li.querySelector('.faceted-filters__filter-link');
          if (!a) return;
          if (li.classList.contains('faceted-filters__filter-list-item--active')) return;
          var textEl = a.querySelector('.faceted-filters__filter-list-item-text');
          var label = (textEl ? textEl.textContent : '').trim();
          var raw = allowed.indexOf(label) === -1 || seen[label];
          if (!raw) seen[label] = true;
          li.style.display = raw ? 'none' : '';
          if (raw) li.setAttribute('data-alj-raw-hidden', 'true');
          else li.removeAttribute('data-alj-raw-hidden');
        });
      });
  }

  /* ─── 7. Legacy Metal-param shim (v3.4) ────────────────────────── */

  function legacyMetalParamShim() {
    var url;
    try { url = new URL(window.location.href); } catch (e) { return false; }
    var OLD = 'filter.v.option.metal';
    var NEW = 'filter.p.m.custom.available_metals';
    var old = url.searchParams.getAll(OLD);
    if (!old.length) return false;
    var have = url.searchParams.getAll(NEW);
    old.forEach(function (v) {
      var t = (v || '').toLowerCase(), b = null;
      if (t.indexOf('yellow') !== -1 && t.indexOf('white') !== -1) b = 'Yellow/White Gold';
      else if (t.indexOf('yellow') !== -1) b = 'Yellow Gold';
      else if (t.indexOf('white') !== -1) b = 'White Gold';
      else if (t.indexOf('rose') !== -1) b = 'Rose Gold';
      else if (t.indexOf('platinum') !== -1) b = 'Platinum';
      else if (t.indexOf('silver') !== -1 || t.indexOf('sterling') !== -1) b = 'Sterling Silver';
      if (b && have.indexOf(b) === -1) {
        url.searchParams.append(NEW, b);
        have.push(b);
      }
    });
    url.searchParams.delete(OLD);
    window.location.replace(url.toString());
    return true;   // navigating away — skip the rest of init
  }

  /* ─── boot ─────────────────────────────────────────────────────── */

  function init() {
    if (legacyMetalParamShim()) return;
    decorateShapeFacets(document);
    decorateMetalDots(document);
    swatchifyPdpMetal(document);
    relocateUmbrella();
    suppressRawSearchFacetValues(document);
    buildFilterDrawer();
    fitSortSelects();
    observer.observe(document.body, { childList: true, subtree: true });
    wireSort();
    wirePopularPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
